import { Home, ShoppingCart, CreditCard, Receipt, DollarSign, Banknote, Calculator, BarChart3, Settings, MoreHorizontal, Users, Landmark, Globe, ArrowLeftRight, Scale } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useOverflowNavigation } from "@/hooks/useOverflowNavigation";
import { useUserRole } from "@/hooks/useUserRole";
import { useNavigationOrder } from "@/hooks/useNavigationOrder";
import type { LucideIcon } from "lucide-react";

// Map nav_key to route config
const navConfig: Record<string, { href: string; icon: LucideIcon }> = {
  'home': { href: '/home', icon: Globe },
  'accounts': { href: '/', icon: Home },
  'sales': { href: '/sales', icon: ShoppingCart },
  'expenditure': { href: '/expenditure', icon: CreditCard },
  'bills': { href: '/bills', icon: Receipt },
  'wages': { href: '/wages', icon: Users },
  'cash-flow': { href: '/cash-flow', icon: Banknote },
  'cash-management': { href: '/cash-management', icon: ArrowLeftRight },
  'payables': { href: '/payables', icon: DollarSign },
  'receivables': { href: '/receivables', icon: Receipt },
  'profit-distribution': { href: '/profit-distribution', icon: Calculator },
  'loans': { href: '/loans', icon: Landmark },
  'trial-balance': { href: '/trial-balance', icon: Scale },
  'reports': { href: '/reports', icon: BarChart3 },
  'settings': { href: '/settings', icon: Settings },
};

const defaultNavigation = [
  { name: "Home", href: "/home", icon: Globe },
  { name: "Accounts", href: "/", icon: Home },
  { name: "Sales", href: "/sales", icon: ShoppingCart },
  { name: "Expenditure", href: "/expenditure", icon: CreditCard },
  { name: "Bills", href: "/bills", icon: Receipt },
  { name: "Wages", href: "/wages", icon: Users },
  { name: "Cash Flow", href: "/cash-flow", icon: Banknote },
  { name: "Cash Management", href: "/cash-management", icon: ArrowLeftRight },
  { name: "Payables", href: "/payables", icon: DollarSign },
  { name: "Receivables", href: "/receivables", icon: Receipt },
  { name: "Profit Distribution", href: "/profit-distribution", icon: Calculator },
  { name: "Loans", href: "/loans", icon: Landmark },
  { name: "Trial Balance", href: "/trial-balance", icon: Scale },
  { name: "Reports", href: "/reports", icon: BarChart3 },
  { name: "Settings", href: "/settings", icon: Settings },
];

export const TopNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, loading } = useUserRole();
  const { data: navOrder } = useNavigationOrder();
  
  // Build navigation array from database order
  const allNavigation = useMemo(() => {
    if (!navOrder || navOrder.length === 0) return defaultNavigation;
    
    return navOrder
      .filter(item => item.is_visible && navConfig[item.nav_key])
      .map(item => ({
        name: item.display_name,
        href: navConfig[item.nav_key].href,
        icon: navConfig[item.nav_key].icon,
      }));
  }, [navOrder]);
  
  // Filter navigation based on user role
  const filteredNavigation = useMemo(() => {
    // Pages that 'user' role can access
    const userAllowedPaths = ['/home', '/', '/sales', '/expenditure', '/bills', '/receivables', '/payables'];
    
    if (loading) {
      return allNavigation.filter(item => userAllowedPaths.includes(item.href));
    }
    
    if (role === 'admin' || role === 'viewer') {
      return allNavigation;
    }
    
    // 'user' role only sees allowed pages
    return allNavigation.filter(item => userAllowedPaths.includes(item.href));
  }, [role, loading, allNavigation]);
  
  const { containerRef, visibleItems, overflowItems, measureItem } = useOverflowNavigation(filteredNavigation);
  
  // Check if current route is in overflow items
  const isOverflowActive = overflowItems.some(
    item => location.pathname === item.href || 
            location.pathname.startsWith(item.href + '/')
  );

  // Callback ref to measure each nav item
  const measureRef = useCallback((node: HTMLAnchorElement | null, index: number) => {
    if (node) {
      measureItem(index, node.offsetWidth + 4); // +4 for gap
    }
  }, [measureItem]);

  return (
    <nav className="border-b bg-card sticky top-0 z-50">
      <div className="px-6 py-3">
        <div ref={containerRef} className="flex items-center gap-1">
          {/* Visible Navigation Items */}
          {visibleItems.map((item, index) => (
            <NavLink
              key={item.name}
              to={item.href}
              end
              ref={(node) => measureRef(node, index)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors whitespace-nowrap"
              activeClassName="bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
            >
              <item.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{item.name}</span>
            </NavLink>
          ))}

          {/* More Dropdown - only show if there are overflow items */}
          {overflowItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap",
                    isOverflowActive
                      ? "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">More</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-card">
                {overflowItems.map((item) => (
                  <DropdownMenuItem
                    key={item.name}
                    onClick={() => navigate(item.href)}
                    className={cn(
                      "flex items-center gap-2 cursor-pointer",
                      location.pathname === item.href && "bg-accent"
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
};
